import { Avatar, Tooltip } from "@mui/material";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import TableLoader from "@/Components/common/TableLoader";
import TableNoContent from "@/Components/common/TableNoContent";
import { POUND_SIGN } from "@/Constants/common";
import { routingConstants } from "@/Constants/routingConstants";
import {
  fetchAppliedCandidates,
  fetchAppliedShifts,
  updateSelectedApplication,
  updateSelectedCandidate,
  updateToggleSelected,
  toggleSelectedAll,
  updateSelectedContractId,
  setAppliedCandidatesLoading,
  updatePagination,
  deSelectAllCandidate,
  updateSorting,
} from "@/reducers/client_candidates/candidatesapplied";
import { TOOLTIPTEXT } from "@/utilities/CommonText";
import {
  expandButtonColumn,
  getColumnNormalHtml,
} from "@/utilities/datatable/columnFunctions";
import useDebounceScroll from "@/utilities/hooks/useDebounceScroll";
import { getComplianceClass } from "@/utilities/utils";

import { Actions } from "./Applied/Actions";
import { FilterModal } from "./Applied/FilterModal";
import { SearchFilter } from "./Applied/SearchFilter";
import IsBankCandidate from "./common/BankCandidate";
import { ContractsShifts } from "./common/ContractsShifts";
import CoverColleagueTag from "./common/CoverColleagueTag";
import { Miles } from "./common/Miles";
import { SelectedCountText } from "./common/SelectedCountText";
import RewardIcon from "../../../../Icons/RewardIcon";
import DataTablePaginationLoader from "../../../../Skeletons/DataTablePaginationLoader";
import ActionIcons from "../../ActionIcons";
import LiveTimerTooltip from "../../contracts/Unfilled/LiveTimerTooltip";
import CandidateStatusAndInfo from "../../dataTables/CandidateStatusAndInfo";
import BlurredText from "../../managedService/BlurredText";

/**
 * Renders the AppliedList component with a list of candidates awaiting shift approval, along with various actions and filters.
 *
 * @return {JSX.Element} The AppliedList component to be rendered
 */
export const AppliedList = () => {
  const dispatch = useDispatch();
  const {
    appliedCandidates,
    selectedCandidate,
    isLoading,
    pagination,
    toggleSelected,
    totalCount,
    isSelectedAll,
    isPaginationLoading,
    sorting,
  } = useSelector((state) => state.clientCandidatesApplied);
  const { isPremiumAccount, clientUserAllPermissions } = useSelector(
    (state) => state.session,
  );
  const [loadingState, setLoadingState] = useState({}); // To store loading states for rows
  const handleRowLoader = (applicant_id, isLoading) => {
    setLoadingState((prevState) => ({
      ...prevState,
      [applicant_id]: isLoading,
    }));
  };

  const [data, setData] = useState([]);
  useEffect(() => {
    if (selectedCandidate.length == 0) {
      dispatch(updateToggleSelected());
    }
  }, [selectedCandidate]);

  useEffect(() => {
    setData(appliedCandidates);
  }, [appliedCandidates]);

  const columns = [
    {
      name: "Candidate",
      sortable: true,
      sortField: "candidate_forename",

      // sortFunction: (erowA, rowB) => sortByText(rowA, rowB, "candidate_name"),
      selector: (row) => (
        <div className="flex gap-[14px]" data-tag="allowRowEvents">
          <div className="min-w-[45px]">
            <Avatar
              alt={row.candidate_name}
              sx={{ width: 45, height: 45 }}
              src={row.profile_image ? row.profile_image : ""}
            />
          </div>
          <div className="flex grow flex-col gap-1 overflow-hidden">
            {!clientUserAllPermissions ||
            clientUserAllPermissions.is_allow_to_view_candidate_profile ===
              1 ? (
              <>
                <Link
                  to={routingConstants.candidateDetail(row.route_id)}
                  className="truncate text-[14px] font-semibold leading-[normal] text-black"
                >
                  {row.candidate_name}
                </Link>
              </>
            ) : (
              <span className="cursor-default truncate text-[14px] font-semibold leading-[normal] text-black">
                {row.candidate_name}
              </span>
            )}
            <CandidateStatusAndInfo
              sortlisted={row.candidate_sortlisted}
              unsuitable={row.candidate_unsuitable}
              is_auto_booked_on_apply={row.is_auto_booked_on_apply}
              extraEle={
                <IsBankCandidate is_bank_candidate={row.is_bank_candidate} />
              }
            />
          </div>
        </div>
      ),
      width: "19%",
    },
    {
      name: "Contact",
      sortable: true,
      sortField: "candidate_email",
      // sortFunction: (rowA, rowB) => sortByText(rowA, rowB, "candidate_email"),
      selector: (row) => (
        <div className="flex flex-col" data-tag="allowRowEvents">
          <BlurredText type="phone">
            {getColumnNormalHtml(row.mobile_phone_number, "truncate")}
          </BlurredText>
          <BlurredText type="email">
            <span className="truncate text-[12px] font-medium leading-[normal] text-txtColor">
              {row.candidate_email}
            </span>
          </BlurredText>
        </div>
      ),
      width: "16%",
    },
    {
      name: "Contract Ref No.",
      sortable: true,
      sortField: "contract_refNo",

      // sortFunction: (rowA, rowB) => sortByText(rowA, rowB, "contract_ref_no"),
      selector: (row) => {
        const publisheddAtMoment = moment.tz(
          row.bank_contract_publish_date,
          "DD/MM/YYYY HH:mm",
          "Europe/London",
        );
        const currentTime = moment.tz("Europe/London");
        const isPublishTimeNotPassed = publisheddAtMoment.isAfter(currentTime)
          ? "1"
          : "0";
        return (
          <div className="flex flex-col gap-1" data-tag="allowRowEvents">
            <div className="flex items-center gap-2">
              {!clientUserAllPermissions ||
              clientUserAllPermissions.is_allow_to_view_contract_detail ===
                1 ? (
                <Link
                  to={routingConstants.contractDetail(row.route_contract_id)}
                  className="text-[14px] font-semibold leading-[normal] text-black"
                >
                  {row.contract_ref_no}
                </Link>
              ) : (
                <span className="line-clamp-1 cursor-default text-[14px] font-semibold leading-[normal] text-black">
                  {row.contract_ref_no}
                </span>
              )}
              {row.is_for_appLocum_candidates ? (
                row.is_bank_contract ? (
                  <LiveTimerTooltip
                    actionedAt={row.contract_created_at}
                    publisheddAt={row.bank_contract_publish_date}
                  />
                ) : null
              ) : null}
            </div>
            {/* Pass the comparison result to IsBankCandidate component */}
            {/* <div>
              <BankContract
                contract_tag={row.contract_tag}
              />
            </div> */}

            {/* < span
              className={`h-[23px]Applied - Remove the "Bank/AppLocum" tag below the 'Contract Ref No. w-[75px] !max-w-full rounded-[4px] text-center text-[14px] font-medium leading-[23px] ${getComplianceClass(
                row.profile_percent,
              )}`}
            >
              {row.profile_percent == "-" ? "0%" : row.profile_percent + "%"}
            </span > */}
            {row?.is_substitute_contract ? (
              <CoverColleagueTag
                proposed_candidate_name={row.proposed_candidate_name}
                proposed_contract_ref={row.proposed_contract_ref}
                hoverType="application"
              />
            ) : // <BankContract
            //   contract_tag={row.contract_tag}
            // />
            null}
          </div>
        );
      },
      width: "12%",
    },
    {
      name: "Role",
      sortable: true,
      sortField: "role_name",

      // sortFunction: (rowA, rowB) => sortByText(rowA, rowB, "role_name"),
      selector: (row) => (
        <div className="flex flex-col" data-tag="allowRowEvents">
          {getColumnNormalHtml(
            row.role_name,
            "line-clamp-2 whitespace-break-spaces",
          )}
          <div className="flex gap-2">
            <Miles miles={row.miles} />
            <span className="text-[12px] font-medium leading-[normal] text-txtColor">
              {row.license_number}
            </span>
          </div>
        </div>
      ),
      width: "14%",
    },
    {
      name: "Date range",
      sortable: true,
      sortField: "contract_start_date",
      // sortFunction: (rowA, rowB) =>
      //   sortByDate(moment(rowA.contract_start_date, "DD/MM/YYYY hh:mm").format("DD/MM/YYYY"),
      //     moment(rowB.contract_end_date, "DD/MM/YYYY hh:mm").format("DD/MM/YYYY"),
      //   ),
      selector: (row) => (
        <div
          className="flex flex-col items-end gap-1"
          data-tag="allowRowEvents"
        >
          {getColumnNormalHtml(
            moment(row.contract_start_date).format("DD/MM/YYYY"),
          )}
          <span className="leading-[0px]">-</span>
          {getColumnNormalHtml(
            moment(row.contract_end_date).format("DD/MM/YYYY"),
          )}
        </div>
      ),
      width: "09%",
    },
    {
      name: "Shift",
      sortable: true,
      sortField: "contract_start_time",

      // sortFunction: (rowA, rowB) =>
      //   sortByTime(
      //     moment(rowA.contract_start_time, "HH:mm:ss").format("HH:mm"),
      //     moment(rowB.contract_end_time, "HH:mm:ss").format("HH:mm"),
      //   ),
      selector: (row) => (
        <div
          className="flex flex-col items-end gap-1"
          data-tag="allowRowEvents"
        >
          {getColumnNormalHtml(
            moment(row.contract_start_time, "HH:mm:ss").format("HH:mm"),
          )}
          <span className="leading-[0px]">-</span>
          {getColumnNormalHtml(
            moment(row.contract_end_time, "HH:mm:ss").format("HH:mm"),
          )}
        </div>
      ),
      width: "6%",
    },
    {
      name: "Days",
      selector: (row) => (
        <div className="grid w-full items-center justify-center gap-1">
          <div className="flex !items-center !justify-center gap-1">
            {getColumnNormalHtml(
              row.total_days_of_contract.toString().padStart(2, "0"),
            )}
            <Tooltip title={TOOLTIPTEXT.viewAppliedDates} placement="top" arrow>
              <button
                className="border-0 bg-none p-0 text-[20px] text-primary outline-none"
                onClick={() => {
                  handleRowLoader(row.applicant_id, true); // Set the loader to true
                  dispatch(
                    fetchAppliedShifts({
                      applicant_id: row.applicant_id,
                      contract_id: row.contract_id,
                      candidate_id: row.candidate_id,
                      forCalendar: true,
                      loading: loadingState[row.applicant_id], // Use individual loading state
                      setLoading: (isLoading) =>
                        handleRowLoader(row.applicant_id, isLoading),
                    }),
                  );
                }}
              >
                {/* {loadingState[row.applicant_id] ? <ButtonLoader/> : <CalendarIcon  /> } */}
                <ActionIcons
                  isLoading={loadingState[row.applicant_id]}
                  type={"viewCalender"}
                />
              </button>
            </Tooltip>
          </div>
          <div>
            <span
              className={`block h-[23px] w-[75px] !max-w-full rounded-[4px] text-center text-[14px] font-medium leading-[23px] ${getComplianceClass(
                row.profile_percent,
              )}`}
            >
              {row.profile_percent == "-" ? "0%" : row.profile_percent + "%"}
            </span>
          </div>
        </div>
      ),
    },
    {
      id: "flex-grow",
      name: "Total",
      selector: (row) => (
        <div className="flex gap-1" data-tag="allowRowEvents">
          {getColumnNormalHtml(
            `${POUND_SIGN}` +
              Number(
                `${
                  row.rate +
                  (row?.cover_request_awarded_reward
                    ? Number(row?.cover_request_awarded_reward)
                    : 0)
                }`,
              ).toFixed(2),
          )}
          {row?.is_substitute_contract &&
          row?.cover_request_awarded_reward != 0 ? (
            <RewardIcon
              cls={"w-[20px] h-[20px] !text-primary cursor-pointer"}
            />
          ) : null}
        </div>
      ),
    },
    expandButtonColumn(),
  ];
  /**
   * Renders a component to display expanded row for the given data.
   *
   * @param {Object} data - The data object containing contract_id and shifts
   * @return {JSX.Element} The rendered ContractsShifts component
   */
  const Expandedrow = ({ data }) => (
    <ContractsShifts
      contract_id={data.contract_id}
      shifts={data.shifts}
      substitueCon={data?.is_substitute_contract}
    />
  );
  const dataTableRef = useRef();

  //Comman function for debounce...
  const handleDebounceScroll = useDebounceScroll(() =>
    dispatch(fetchAppliedCandidates({})),
  );
  useEffect(() => {
    if (dataTableRef.current) {
      const table = dataTableRef.current.querySelector("._DataTable");
      table.addEventListener("scroll", handleDebounceScroll);
      return () => {
        table.removeEventListener("scroll", handleDebounceScroll);
      };
    }
  }, [handleDebounceScroll]);

  const handleSort = ({ sortField: newSortField }) => {
    const { sortByColumn, sortByOrder } = sorting; // get from Redux

    let newSortOrder = "asc";
    if (sortByColumn === newSortField) {
      newSortOrder = sortByOrder === "asc" ? "desc" : "asc";
    }

    dispatch(
      updateSorting({ sortByColumn: newSortField, sortByOrder: newSortOrder }),
    );
    dispatch(setAppliedCandidatesLoading(true));
    dispatch(deSelectAllCandidate());
    dispatch(
      updatePagination({
        ...pagination,
        currentPage: 1,
        isLastPage: false,
      }),
    );
    dispatch(fetchAppliedCandidates());
  };

  return (
    <div className="AppliedList_Wrapper mb-[58px]">
      <div className="flex h-full flex-col rounded-[10px] bg-white p-4 shadow-[0px_x1px_6px_0px_#E7EFFB]">
        <div className="mb-[20px] flex flex-col flex-wrap gap-3 border-b border-b-[#E8E8E8] pb-[11px] md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-2 md:gap-[21px]">
            <h4 className="main-heading font-semibold leading-[normal] text-black">
              Candidates Awaiting Shift Approval
            </h4>
            <Actions />
            <SelectedCountText
              selectedCount={selectedCandidate.length}
              totalCount={totalCount}
              isSelectedAll={isSelectedAll}
              clearSelected={() => {
                dispatch(updateToggleSelected());
              }}
              handleSelectAll={(val) => {
                dispatch(toggleSelectedAll(val));
              }}
              updateSelected={(val) => {
                dispatch(updateSelectedCandidate(val));
              }}
              currentDataLength={data.length}
            />
          </div>
          <div className="ml-4 flex gap-[20px]">
            <SearchFilter />
            <FilterModal />
          </div>
        </div>
        <div
          className="DataTable_Wrapper hide_extended_arrow"
          ref={dataTableRef}
        >
          <DataTable
            columns={columns}
            data={data}
            onSort={handleSort}
            // persistTableHead={true}
            fixedHeader
            fixedHeaderScrollHeight="600px"
            progressPending={isLoading}
            progressComponent={<TableLoader rows={5} />}
            selectableRows
            onSelectedRowsChange={(state) => {
              let selectedIDS = state.selectedRows.map((item) => {
                return item.candidate_id;
              });
              let applicationIDs = state.selectedRows.map((item) => {
                return item.applicant_id;
              });
              let contract_id = state.selectedRows.map((item) => {
                return item.contract_id;
              });
              dispatch(updateSelectedApplication(applicationIDs));
              dispatch(updateSelectedCandidate(selectedIDS));
              dispatch(updateSelectedContractId(contract_id));
            }}
            noDataComponent={<TableNoContent rows={5} />}
            clearSelectedRows={toggleSelected}
            expandableRows
            expandOnRowClicked
            expandableRowsComponent={Expandedrow}
            className="_DataTable"
            onRowExpandToggled={(state, row) => {
              if (state) {
                dispatch(
                  fetchAppliedShifts({
                    contract_id: row.contract_id,
                    candidate_id: row.candidate_id,
                  }),
                );
              }
            }}
          />
        </div>
        {!isLoading && isPaginationLoading ? (
          <DataTablePaginationLoader />
        ) : null}
      </div>
    </div>
  );
};
